import {
    ALPHA_2_ISO_3166,
    APPLICANT_CREDIT_RATING_REQUESTED_STATUSES,
    EMPLOYMENT_INDUSTRY,
    LIVING_SITUATIONS,
    PERSONAL_STATUS,
    RESIDENCE_PERMIT_TYPES,
    SALUTATIONS,
    TAX_CLASSIFICATIONS,
    TYPE_OF_EMPLOYMENT,
} from 'cpool-constants';
import { Applicant } from 'cpool-schemas';
import type { z } from 'zod';

import { censorApplicant } from '../../../utils/censor.utils';
import type { EventSchema } from '../../../utils/EventSchema';
import { _Event } from '../../_Event';

export class LoanRequest_ApplicantCreated extends _Event {
    constructor(payload: EventSchema<LoanRequest_ApplicantCreated>, deprecated = false) {
        super(payload, deprecated);
    }

    getSchema() {
        return Applicant;
    }

    getCensoredSchema() {
        return this.getSchema().transform((applicant) => censorApplicant(applicant));
    }

    getCensoredSchemaApplicant2Only(applicant2Id: string) {
        return this.getSchema().transform((applicant) =>
            applicant.id === applicant2Id ? censorApplicant(applicant) : applicant,
        );
    }

    static example(): EventSchema<LoanRequest_ApplicantCreated> {
        return {
            advertisementConsentAgreed: false,
            beschaeftigung: {
                angestellter: {
                    beschaeftigungsverhaeltnis: {
                        arbeitgeber: {
                            anschrift: {
                                hausnummer: '837',
                                land: ALPHA_2_ISO_3166.AD,
                                ort: 'Hameln',
                                plz: '31785',
                                strasse: 'Dessauer',
                            },
                            branche: EMPLOYMENT_INDUSTRY.HANDEL,
                            name: 'IT GmbH',
                        },
                        befristetBis: new Date('2023-07-18'),
                        befristung: TYPE_OF_EMPLOYMENT.BEFRISTET,
                        berufsbezeichnung: 'Software Engineer',
                        beschaeftigtSeit: new Date('2023-07-17'),
                        inProbezeit: true,
                        inProbezeitBis: new Date('2023-07-18'),
                        kumulierterJahreswert: 1000,
                        nettoeinkommenMonatlich: 1000,
                        nettoeinkommenVorletzterMonat: 1000,
                        nettoeinkommenVorVorletzterMonat: 1000,
                    },
                },
                beschaeftigungsart: 'ANGESTELLTER',
                steuerklasse: TAX_CLASSIFICATIONS.CLASS_1,
            },
            creditRatingRequestedStatus: APPLICANT_CREDIT_RATING_REQUESTED_STATUSES.NOT_REQUESTED,
            dataProtectionAgreed: false,
            emailVerified: false,
            id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
            legitimation: {
                telephone: [
                    {
                        legitimation: true,
                        timestamp: new Date('2023-07-17').getTime(),
                    },
                ],
            },
            personendaten: {
                anrede: SALUTATIONS.HERR,
                arbeitserlaubnisBefristetBis: new Date('2023-07-18'),
                arbeitserlaubnisVorhanden: true,
                aufenthaltBefristetBis: new Date('2023-07-18'),
                aufenthaltstitel: RESIDENCE_PERMIT_TYPES.RESIDENCE_PERMIT_OTHER,
                email: 'something@example.com',
                familienstand: PERSONAL_STATUS.LEDIG,
                geburtsdatum: new Date('2023-07-17'),
                geburtsland: ALPHA_2_ISO_3166.AD,
                geburtsname: 'John',
                geburtsort: 'Hameln',
                inDeutschlandSeit: new Date('2023-07-17'),
                nachname: 'Doe',
                staatsangehoerigkeit: ALPHA_2_ISO_3166.AD,
                telefonPrivat: '+4917675879507',
                vorname: 'John',
            },
            wohnsituation: {
                anschrift: {
                    hausnummer: '837',
                    land: ALPHA_2_ISO_3166.AD,
                    ort: 'Hameln',
                    plz: '18059',
                    strasse: 'Dessauer',
                    wohnhaftSeit: new Date('2023-07-17'),
                },
                anzahlPersonenImHaushalt: 500,
                anzahlPkw: 2,
                gemeinsamerHaushalt: false,
                vorherigeAnschrift: {
                    hausnummer: '837',
                    land: ALPHA_2_ISO_3166.AD,
                    ort: 'Hameln',
                    plz: '18059',
                    strasse: 'Dessauer',
                    wohnhaftSeit: new Date('2023-07-17'),
                },
                wohnart: LIVING_SITUATIONS.ZUR_MIETE,
                wohnflaeche: 1000,
            },
        };
    }

    static exampleCensored(): z.infer<ReturnType<LoanRequest_ApplicantCreated['getCensoredSchema']>> {
        return censorApplicant(LoanRequest_ApplicantCreated.example());
    }

    static exampleCensoredApplicant2Only(applicant2Id: string) {
        const example = LoanRequest_ApplicantCreated.example();
        return example.id === applicant2Id ? censorApplicant(example) : example;
    }
}
